import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchLeague,
  fetchCategories,
  fetchDivisionsForSeason,
  fetchSeasons,
} from '../../store/slices/leagueSlice';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import LeagueInfo from '../../components/league/LeagueInfo';
import SeasonInfo from '../../components/league/SeasonInfo';
import DivisionList from '../../components/league/DivisionList';
import LeagueAdminManager from '../../components/admin/LeagueAdminManager';
import { UserRole, SeasonStatus } from '@sulis-liiga/common';
import { resolveViewingSeason } from '../../utils/season';

const LeagueDetailPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { leagueId } = useParams<{ leagueId: string }>();
  const { currentLeague, currentSeason, divisions, seasons, loading, error } = useSelector(
    (state: RootState) => state.league,
  );
  const { user } = useSelector((state: RootState) => state.auth);

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  const canManageLeague =
    user?.roles.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
    user?.roles.includes(UserRole.SUPER_ADMINISTRATOR);

  const isSuperAdmin = user?.roles.includes(UserRole.SUPER_ADMINISTRATOR);

  // The season chosen in the dropdown; defaults to the league's active season.
  const [selectedSeasonId, setSelectedSeasonId] = useState('');

  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      dispatch(fetchCategories(leagueId));
      dispatch(fetchSeasons(leagueId));
    }
  }, [dispatch, leagueId, tenantResolved]);

  // Seasons offered in the dropdown. Upcoming seasons are shown only to admins;
  // everyone sees active + completed. The loaded currentSeason is folded in as a
  // fallback in case the list hasn't arrived yet. Ordered active → upcoming →
  // completed, then by most-recent start date.
  const visibleSeasons = useMemo(() => {
    const list = [...seasons];
    if (currentSeason && !list.some(s => s.id === currentSeason.id)) list.push(currentSeason);
    const order: Record<string, number> = {
      [SeasonStatus.ACTIVE]: 0,
      [SeasonStatus.UPCOMING]: 1,
      [SeasonStatus.COMPLETED]: 2,
    };
    return list
      .filter(s => s.status !== SeasonStatus.UPCOMING || canManageLeague)
      .sort(
        (a, b) =>
          (order[a.status] ?? 3) - (order[b.status] ?? 3) ||
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
      );
  }, [seasons, currentSeason, canManageLeague]);

  // Default the selection to the latest active season, else the latest completed
  // one (UPCOMING is never the default). When neither exists — e.g. a brand-new
  // league with just an upcoming season, visible to admins — fall back to the
  // first listed season, or to the league's current season while the seasons
  // list is still loading, so the page isn't empty.
  useEffect(() => {
    if (selectedSeasonId) return;
    const pick = resolveViewingSeason(seasons);
    if (pick) {
      setSelectedSeasonId(pick.id);
    } else if (visibleSeasons.length > 0) {
      setSelectedSeasonId(visibleSeasons[0].id);
    } else if (currentLeague?.currentSeasonId) {
      setSelectedSeasonId(currentLeague.currentSeasonId);
    }
  }, [selectedSeasonId, seasons, visibleSeasons, currentLeague?.currentSeasonId]);

  // Load ALL divisions (across categories) for the selected season.
  useEffect(() => {
    if (leagueId && tenantResolved && selectedSeasonId) {
      dispatch(fetchDivisionsForSeason({ leagueId, seasonId: selectedSeasonId }));
    }
  }, [dispatch, leagueId, tenantResolved, selectedSeasonId]);

  const selectedSeason = visibleSeasons.find(s => s.id === selectedSeasonId) ?? null;
  const isActiveSeasonSelected =
    !!selectedSeasonId && selectedSeasonId === currentLeague?.currentSeasonId;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white shadow rounded-lg p-6">
          <div className="rounded-md bg-danger-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-danger-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-danger-800">{error}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentLeague) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to="/leagues"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <svg
              className="mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Leagues
          </Link>
        </div>

        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              {currentLeague.name}
            </h2>
          </div>
          {canManageLeague && (
            <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
              <Link
                to={`/leagues/${leagueId}/edit`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Edit League
              </Link>
              <Link
                to={`/leagues/${leagueId}/seasons/create`}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Add Season
              </Link>
              {currentLeague.currentSeasonId && (
                <Link
                  to={`/leagues/${leagueId}/seasons/${currentLeague.currentSeasonId}/transition`}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Season Transition
                </Link>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <LeagueInfo league={currentLeague} />

          {/* Season selector + selected-season info at the top */}
          {visibleSeasons.length > 0 && (
            <div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <div className="flex items-center gap-3">
                  <label htmlFor="season-select" className="text-sm font-medium text-gray-700">
                    Season
                  </label>
                  <select
                    id="season-select"
                    value={selectedSeasonId}
                    onChange={e => setSelectedSeasonId(e.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {visibleSeasons.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.status})
                      </option>
                    ))}
                  </select>
                </div>
                {canManageLeague && selectedSeasonId && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/leagues/${leagueId}/seasons/${selectedSeasonId}`}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Manage season
                    </Link>
                    {isActiveSeasonSelected && (
                      <Link
                        to={`/leagues/${leagueId}/seasons/${selectedSeasonId}/re-register`}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Re-register a team
                      </Link>
                    )}
                  </div>
                )}
              </div>
              {selectedSeason && <SeasonInfo season={selectedSeason} />}
            </div>
          )}

          {/* Divisions for the selected season — compact per-category banners */}
          {divisions.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Divisions</h3>
                {canManageLeague && selectedSeasonId && (
                  <Link
                    to={`/leagues/${leagueId}/seasons/${selectedSeasonId}/divisions/create`}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Add Division
                  </Link>
                )}
              </div>
              <DivisionList
                divisions={divisions}
                groupByCategory
                compact
                leagueId={leagueId}
                seasonId={selectedSeasonId}
                canManage={canManageLeague}
              />
            </div>
          )}

          {divisions.length === 0 && selectedSeasonId && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">No divisions yet</h3>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>This season doesn't have any divisions set up yet.</p>
                </div>
                {canManageLeague && (
                  <div className="mt-5">
                    <Link
                      to={`/leagues/${leagueId}/seasons/${selectedSeasonId}/divisions/create`}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Create Division
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Super-Admin-only administrator management section */}
          {isSuperAdmin && leagueId && <LeagueAdminManager leagueId={leagueId} />}
        </div>
      </div>
    </div>
  );
};

export default LeagueDetailPage;
